import { EmptyState } from "@/components/ui/empty-state";
import { useTeams } from "@/contexts/teams-provider";
import { useTranslation } from "@/hooks/useTranslation";
import { PromptCategoryId, PromptDTO } from "@shaarp/common";
import PageSkeleton from "../page-skeleton";
import EmptyPromptSection from "./empty-prompt-section";
import PromptsSection from "./prompts-section";

type PromptLibraryContentProps = {
  isLoading: boolean;
  error: Error | null;
  hasResults: boolean;
  searchQuery: string;
  filteredFavoritePrompts: PromptDTO[];
  filteredPinnedPrompts: PromptDTO[];
  filteredUserPrompts: PromptDTO[];
  filteredTeamPrompts: PromptDTO[];
  publicPromptsByCategory: Map<PromptCategoryId, PromptDTO[]>;
  favoriteIds: Set<string>;
  pinnedIds: Set<string>;
  onSelectPrompt: (prompt: PromptDTO) => void;
  onEditPrompt: (prompt: PromptDTO) => void;
  onRemovePrompt: (prompt: PromptDTO) => void;
  onToggleFavorite: (prompt: PromptDTO) => void;
  onTogglePinned: (prompt: PromptDTO) => void;
  onCreatePersonalPrompt: () => void;
  onCreateTeamPrompt: () => void;
  filterPrompts: (prompts: PromptDTO[]) => PromptDTO[];
  PROMPT_CATEGORY_IDS: PromptCategoryId[];
};

export function PromptLibraryContent({
  isLoading,
  error,
  hasResults,
  searchQuery,
  filteredFavoritePrompts,
  filteredPinnedPrompts,
  filteredUserPrompts,
  filteredTeamPrompts,
  publicPromptsByCategory,
  favoriteIds,
  pinnedIds,
  onSelectPrompt,
  onEditPrompt,
  onRemovePrompt,
  onToggleFavorite,
  onTogglePinned,
  onCreatePersonalPrompt,
  onCreateTeamPrompt,
  filterPrompts,
  PROMPT_CATEGORY_IDS,
}: PromptLibraryContentProps) {
  const { t } = useTranslation();
  const { teamSelected } = useTeams();

  if (isLoading) {
    return <PageSkeleton />;
  }

  if (error) {
    return (
      <EmptyState
        title={t("prompt.select.error.title")}
        description={error?.message || t("prompt.select.error.unknown")}
        className="py-8"
      />
    );
  }

  if (!hasResults && searchQuery.trim()) {
    return (
      <EmptyState
        title={t("prompt.select.noResults.title")}
        description={t("prompt.select.noResults.description")}
        className="py-8"
      />
    );
  }

  return (
    <>
      {/* Pinned Section */}
      {filteredPinnedPrompts.length > 0 && (
        <PromptsSection
          prompts={filteredPinnedPrompts}
          onSelectPrompt={onSelectPrompt}
          onEditPrompt={onEditPrompt}
          onRemovePrompt={onRemovePrompt}
          initialVisibleCount={4}
          title={t("prompt.library.tabs.pinned")}
          description={t("prompt.library.pinned.description")}
          sectionId="pinned"
          favoritePromptIds={favoriteIds}
          onToggleFavorite={onToggleFavorite}
          pinnedPromptIds={pinnedIds}
          onTogglePinned={onTogglePinned}
        />
      )}

      {/* Favorites Section */}
      {filteredFavoritePrompts.length > 0 && (
        <PromptsSection
          prompts={filteredFavoritePrompts}
          onSelectPrompt={onSelectPrompt}
          onEditPrompt={onEditPrompt}
          onRemovePrompt={onRemovePrompt}
          initialVisibleCount={4}
          title={t("prompt.library.tabs.favorites")}
          description={t("prompt.library.favorites.description")}
          sectionId="favorites"
          favoritePromptIds={favoriteIds}
          onToggleFavorite={onToggleFavorite}
          pinnedPromptIds={pinnedIds}
          onTogglePinned={onTogglePinned}
        />
      )}

      {/* Personal Prompts Section */}
      {filteredUserPrompts.length > 0 ? (
        <PromptsSection
          prompts={filteredUserPrompts}
          onSelectPrompt={onSelectPrompt}
          onEditPrompt={onEditPrompt}
          onRemovePrompt={onRemovePrompt}
          initialVisibleCount={4}
          title={t("prompt.library.tabs.personal")}
          description={t("prompt.library.custom.descriptionPersonal")}
          sectionId="user"
          favoritePromptIds={favoriteIds}
          onToggleFavorite={onToggleFavorite}
          pinnedPromptIds={pinnedIds}
          onTogglePinned={onTogglePinned}
        />
      ) : (
        <EmptyPromptSection
          ownerType="user"
          onCreateClick={onCreatePersonalPrompt}
        />
      )}

      {/* Team Prompts Section */}
      {filteredTeamPrompts.length > 0 ? (
        <PromptsSection
          prompts={filteredTeamPrompts}
          onSelectPrompt={onSelectPrompt}
          onEditPrompt={onEditPrompt}
          onRemovePrompt={onRemovePrompt}
          initialVisibleCount={4}
          title={t("prompt.library.tabs.team")}
          description={t("prompt.library.custom.descriptionTeam", {
            teamName: teamSelected?.name ?? t("prompt.library.tabs.team"),
          })}
          sectionId="team"
          favoritePromptIds={favoriteIds}
          onToggleFavorite={onToggleFavorite}
          pinnedPromptIds={pinnedIds}
          onTogglePinned={onTogglePinned}
        />
      ) : (
        <EmptyPromptSection
          ownerType="team"
          teamName={teamSelected?.name}
          onCreateClick={onCreateTeamPrompt}
        />
      )}

      {/* Public Prompts by Categories */}
      {PROMPT_CATEGORY_IDS.map((categoryId) => {
        const categoryPrompts = publicPromptsByCategory.get(categoryId) || [];
        const filteredPrompts = filterPrompts(categoryPrompts);
        if (filteredPrompts.length === 0) return null;

        return (
          <PromptsSection
            key={categoryId}
            prompts={filteredPrompts}
            onSelectPrompt={onSelectPrompt}
            onEditPrompt={onEditPrompt}
            onRemovePrompt={onRemovePrompt}
            initialVisibleCount={4}
            title={t(`prompt.categories.${categoryId}.name`)}
            description={t(`prompt.categories.${categoryId}.description`)}
            sectionId={`category-${categoryId}`}
            favoritePromptIds={favoriteIds}
            onToggleFavorite={onToggleFavorite}
            pinnedPromptIds={pinnedIds}
            onTogglePinned={onTogglePinned}
            categoryId={categoryId}
          />
        );
      })}
    </>
  );
}
